import { ArrowLeft, BookOpen, Calendar, Clock, Languages } from 'lucide-react';
import { Link, useParams } from 'react-router';

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@workspace/shadcn';
import { useData } from '@workspace/ui';

import {
  courseImage,
  formatDateRange,
  formatDuration,
  formatLevel,
} from '../lib/format.ts';

export default function CourseDetail() {
  const { slug = '' } = useParams();
  const { data: course, isLoading, isError } = useData(
    'GET /courses/{slug}',
    { slug },
    { retry: false },
  );

  if (isError) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <p className="text-muted-foreground">
          We could not load that course.{' '}
          <Link to="/" className="text-primary">
            Back to my courses
          </Link>
        </p>
      </div>
    );
  }

  if (isLoading || !course) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  const lessonCount = course.modules.reduce(
    (total, module) => total + module.lessons.length,
    0,
  );
  const duration = formatDuration(course.durationHours);

  return (
    <div className="mx-auto w-full max-w-4xl">
      <Link
        to="/"
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="size-4" />
        My Courses
      </Link>

      <div className="aspect-16/9 w-full overflow-hidden rounded-xl">
        <img
          src={courseImage(course.imageUrl)}
          alt={course.title}
          className="h-full w-full object-cover"
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {course.category && <Badge>{course.category}</Badge>}
        <Badge variant="outline">{formatLevel(course.level)}</Badge>
      </div>

      <h1 className="mt-3 text-3xl font-bold">{course.title}</h1>
      <p className="text-muted-foreground mt-1 text-lg">{course.oneLiner}</p>

      <div className="text-muted-foreground mt-4 flex flex-wrap gap-4 text-sm">
        <span className="flex items-center gap-1">
          <Calendar className="size-4" />
          {formatDateRange(course.startDate, course.endDate)}
        </span>
        {duration && (
          <span className="flex items-center gap-1">
            <Clock className="size-4" />
            {duration}
          </span>
        )}
        {course.languages.length > 0 && (
          <span className="flex items-center gap-1">
            <Languages className="size-4" />
            {course.languages.join(', ')}
          </span>
        )}
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>About this course</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-foreground/90">{course.description}</p>
        </CardContent>
      </Card>

      {course.objectives.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>What you will learn</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-foreground/90 list-disc space-y-2 ps-5">
              {course.objectives.map((objective) => (
                <li key={objective}>{objective}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {course.modules.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Curriculum</CardTitle>
            <p className="text-muted-foreground text-sm">
              {course.modules.length} modules &bull; {lessonCount} lessons
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {course.modules.map((module) => (
              <div key={module.id}>
                <h3 className="mb-2 flex items-center gap-2 font-semibold">
                  <BookOpen className="text-primary size-4" />
                  {module.title}
                </h3>
                <ul className="space-y-2">
                  {module.lessons.map((lesson) => (
                    <li
                      key={lesson.id}
                      className="border-border flex items-start justify-between gap-4 rounded-lg border p-3"
                    >
                      <div>
                        <p className="text-sm font-medium">{lesson.title}</p>
                        {lesson.description && (
                          <p className="text-muted-foreground mt-1 text-sm">
                            {lesson.description}
                          </p>
                        )}
                      </div>
                      <span className="text-muted-foreground shrink-0 text-xs whitespace-nowrap">
                        {lesson.duration}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
